import React, {useEffect, useMemo, useReducer} from "react";
import {useFormik} from "formik";
import * as yup from "yup";

import {addDays, endOfDay, endOfToday, formatISO, subDays} from "date-fns";
import {dateAndTime, dmyString} from "../../../services/date/formatters";
import {useGlobalState} from "../../../GlobalState";
import {isValidDateObject} from "../../../services/date/validators";
import QuoteService from "../../../services/quotes";

import Typography from "@mui/material/Typography";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import ListItemText from "@mui/material/ListItemText";
import RadioGroup from "@mui/material/RadioGroup";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import {DatePicker} from "@mui/x-date-pickers";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import EditIcon from "@mui/icons-material/Edit";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import Grid from "@mui/material/Grid";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";


const defaultPaymentPolicy = QuoteService.enums.PAYMENT_POLICY.hostCustomized;
const onlinePaymentEnabled = false;

export const useStepBookingConditions = ({arrival, onSubmit = f => f}) => {
    const {settings} = useGlobalState();

    const defaultConfirmationPolicy = settings.defaultConfirmationPolicy;
    const defaultCancellationPolicy = settings.defaultCancellationPolicy;
    const defaultDepositPercentage = settings.defaultDepositPercentage;  // default value for the deposit percentage
    const defaultExpirationInDays = settings.defaultQuoteExpirationInDays;
    const defaultDaysForewarningForRefund = settings.defaultForewarningForRefundInDays;  // default number of day to compute the refundable until value
    const defaultTouristTaxPolicy = settings.defaultTouristTaxPolicy;
    const defaultTouristTaxUnitPrice = settings.defaultTouristTaxUnitPrice;

    const initialRefundableUntil = useMemo(
        () => {
            const today = endOfToday();
            const refundableUntil = isValidDateObject(arrival) ?
                endOfDay(subDays(arrival, defaultDaysForewarningForRefund)) : today;
            return refundableUntil > today ? refundableUntil : today;
        },
        [arrival, defaultDaysForewarningForRefund]
    );

    const initialExpiration = useMemo(
        () => {
            const expiration = addDays(new Date(), defaultExpirationInDays);
            return expiration < arrival ? expiration : arrival;
        },
        [arrival, defaultExpirationInDays]
    )

    const formik = useFormik({
            initialValues: {
                paymentPolicy: defaultPaymentPolicy,
                confirmationPolicy: defaultConfirmationPolicy,
                cancellationPolicy: defaultCancellationPolicy,
                depositPercentage: defaultDepositPercentage,
                refundableUntil: initialRefundableUntil,
                expiration: initialExpiration,
                touristTaxPolicy: defaultTouristTaxPolicy,
                touristTaxUnitPrice: defaultTouristTaxUnitPrice,
            },
            validationSchema: yup.object().shape({
                paymentPolicy: yup.string().required('Campo obbligatorio'),
                cancellationPolicy: yup.string().required('Campo obbligatorio'),
                confirmationPolicy: yup.string().required('Campo obbligatorio'),
                depositPercentage: yup.number()
                    .when('confirmationPolicy', {
                        is: QuoteService.enums.CONFIRMATION_POLICY.deposit,
                        then: yup.number()
                            .integer()
                            .min(0, 'Deve essere maggiore o uguale a 0')
                            .max(100, 'Deve essere minore o uguale a 100')
                            .typeError('Deve essere un numero')
                            .required('Campo obbligatorio')
                    }),
                refundableUntil: yup.date()
                    .when('cancellationPolicy', {
                        is: QuoteService.enums.CANCELLATION_POLICY.timeRestrictedRefundable,
                        then: yup.date().typeError('Deve essere una data valida gg/mm/aaaa')
                            .required('Campo obbligatorio'),
                    }),
                expiration: yup.date().typeError('Deve essere una data valida gg/mm/aaaa')
                    .required('Campo obbligatorio'),
                touristTaxPolicy: yup.string().required('Campo obbligatorio'),
                touristTaxUnitPrice: yup.number()
                    .typeError('Deve essere un numero')
                    .min(0, 'Deve essere maggiore o uguale a 0')
                    .required('Campo obbligatorio'),
            }),
            onSubmit: (values) => {
                let {expiration, ...bookingCondition} = values;
                if(bookingCondition.confirmationPolicy !== QuoteService.enums.CONFIRMATION_POLICY.deposit){
                    bookingCondition.depositPercentage = null;
                }
                if(bookingCondition.cancellationPolicy !== QuoteService.enums.CANCELLATION_POLICY.timeRestrictedRefundable){
                    bookingCondition.refundableUntil = null;
                }
                onSubmit({
                    expiration: formatISO(values.expiration),
                    bookingCondition: {
                        ...bookingCondition,
                        refundableUntil: bookingCondition.refundableUntil !== null ? formatISO(bookingCondition.refundableUntil) : null
                    }
                });
            },
        }
    );

    // Whenever the initialRefundableUntil or defaultExpiration dates change (due to the change of the arrival)
    // reset their value to the default initial values.
    // Moreover, if the initialRefundableUntil is equal to today set the cancellation policy to not refundable
    // since it doesn't make sense to have 0 days to ask for a refund
    useEffect(
        () => {
            formik.setFieldValue("refundableUntil", initialRefundableUntil);
            if(initialRefundableUntil <= endOfToday() && defaultCancellationPolicy === QuoteService.enums.CANCELLATION_POLICY.timeRestrictedRefundable){
                formik.setFieldValue("cancellationPolicy", QuoteService.enums.CANCELLATION_POLICY.notRefundable);
            }else{
                formik.setFieldValue("cancellationPolicy", defaultCancellationPolicy);
            }
        },
        [initialRefundableUntil]  // eslint-disable-line react-hooks/exhaustive-deps
    )
    useEffect(
        () => {formik.setFieldValue("expiration", initialExpiration)},
        [initialExpiration]  // eslint-disable-line react-hooks/exhaustive-deps
    )

    return {formik};
}

const StepBookingConditions = ({arrival, formik}) => {
    const [confirmationPolicyOpen, toggleConfirmationPolicyOpen] = useReducer(state => !state, false);
    const [cancellationPolicyOpen, toggleCancellationPolicyOpen] = useReducer(state => !state, false);
    const [expirationOpen, toggleExpirationOpen] = useReducer(state => !state, false);
    const [touristTaxPolicyOpen, toggleTouristTaxPolicyOpen] = useReducer(state => !state, false);

    const today = new Date();

    const onlinePaymentDesc = `L'ospite potrà confermare e pagare la prenotazione direttamente dal preventivo tramite 
un link (non sarà necessario che comunichi verbalmente o via mail la sua intenzione di prenotare). Non dovrai gestire
pagamenti, caparre e rimborsi, avverrà tutto in maniera automatica.
Una mail di conferma ti verrà inviata qualora l'ospite confermasse il preventivo.`

    const getDepositPercentage = () => {
        if(formik.values.depositPercentage !== ""){
            return formik.values.depositPercentage;
        }else{
            return "--"
        }
    }

    const getRefundableUntil = (format = 'dmyString') => {
        if (isValidDateObject(formik.values.refundableUntil)) {
            if (format === 'dateAndTime') {
                return dateAndTime(formik.values.refundableUntil);
            } else {
                return dmyString(formik.values.refundableUntil);
            }
        } else {
            return "--";
        }
    }

    const getExpiration = () => {
        if (isValidDateObject(formik.values.expiration)) {
            return dmyString(formik.values.expiration);
        } else {
            return '--';
        }
    }

    const confirmationPolicy = {
        [QuoteService.enums.CONFIRMATION_POLICY.deposit]: {
            description: `Per prenotare è richiesto il pagamento della caparra pari 
            al ${getDepositPercentage()}% dell'importo totale.`,
            label: "Caparra",
            title: `Pagamento caparra del ${getDepositPercentage()}%`
        },
        [QuoteService.enums.CONFIRMATION_POLICY.prePaid]: {
            description: "Per pronotare è richiesto il pagamento dell'intero importo.",
            label: "Pagamento importo anticipato",
            title: "Pagamento anticipato dell'importo"
        },
        [QuoteService.enums.CONFIRMATION_POLICY.creditCardGuarantee]: {
            description: "Per pronotare è richiesta la carta di credito a scopo di garanzia, l'importo verrà prelevato successivamente",
            label: "Carta di credito a garanzia",
            title: "Carta di credito a garanzia"
        },
        [QuoteService.enums.CONFIRMATION_POLICY.none]: {
            description: "Non verrà indicata nessuna condizione per pronotare",
            label: "Nessuna (non specificata)",
            title: "Nessuna (non specificata)"
        },
    }

    const cancellationPolicy = {
        [QuoteService.enums.CANCELLATION_POLICY.notRefundable]: {
            description: "La prenotazione non prevede alcun rimborso.",
            label: "Non rimborsabile",
            title: "Non rimborsabile"
        },
        [QuoteService.enums.CANCELLATION_POLICY.totallyRefundable]: {
            description: "La prenotazione può essere cancellata gratuitamente fino al giorno d'arrivo con restituzione dell'importo pagato.",
            label: "Rimborsabile",
            title: "Rimborsabile"
        },
        [QuoteService.enums.CANCELLATION_POLICY.timeRestrictedRefundable]: {
            description: `La prenotazione può essere cancellata gratuitamente con rimborso dell'importo pagato fino 
            a ${getRefundableUntil('dateAndTime')}. Da ${getRefundableUntil('dateAndTime')} in poi la prenotazione non potrà 
            essere cancellata e non verrà effettutao alcun rimborso.`,
            label: `Rimborsabile (con limite di tempo)`,
            title: `Rimborsabile fino al ${getRefundableUntil()}`
        },
        [QuoteService.enums.CANCELLATION_POLICY.none]: {
            description: "Non verrà indicata nessuna condizione per la cancellazione",
            label: "Nessuna (non specificata)",
            title: "Nessuna (non specificata)"
        },
    }

    const touristTaxPolicy = {
        [QuoteService.enums.TOURIST_TAX_POLICY.none]: {
            description: "Non verrà indicata nessuna politica per la tassa di soggiorno",
            label: "Nessuna (non specificata)",
            title: "Nessuna (non specificata)"
        },
        [QuoteService.enums.TOURIST_TAX_POLICY.included]: {
            description: 'La tassa di soggiorno è inclusa nel prezzo totale della prenotazione',
            label: "Inclusa nel prezzo",
            title: `Inclusa nel prezzo`
        },
        [QuoteService.enums.TOURIST_TAX_POLICY.excluded]: {
            description: 'La tassa di soggiorno non è inclusa nel prezzo totale della prenotazione',
            label: "Esclusa dal prezzo",
            title: `Esclusa dal prezzo`
        },
    }

    // Compute the compatible conditions according to the current host choices.
    // This is done because some policies are not compatible each other.
    // First filter by the chosen paymentPolicy, then by the confirmationPolicy, and lastly by the cancellationPolicy
    const compatibleConfirmationPolicy = useMemo(
        () => [...new Set(QuoteService.bookingConditions
            .filter(c => c.paymentPolicy === formik.values.paymentPolicy)
            .map((c => c.confirmationPolicy)))
        ], [formik.values.paymentPolicy]
    );
    const compatibleCancellationPolicy = useMemo(
        () => [...new Set(QuoteService.bookingConditions
            .filter(c => c.paymentPolicy === formik.values.paymentPolicy)
            .filter((c => c.confirmationPolicy === formik.values.confirmationPolicy || formik.values.confirmationPolicy === ""))
            .map((c => c.cancellationPolicy)))],
        [formik.values.paymentPolicy, formik.values.confirmationPolicy]
    );

    const handleChangePaymentPolicy = () => {
        const newValue = formik.values.paymentPolicy === QuoteService.enums.PAYMENT_POLICY.online ? QuoteService.enums.PAYMENT_POLICY.hostCustomized : QuoteService.enums.PAYMENT_POLICY.online;
        formik.setFieldValue("paymentPolicy", newValue)
    };

    // Check compatibility and eventually nullify a choice whenever a choice is made
    useEffect(
        () => {
            if (!compatibleConfirmationPolicy.includes(formik.values.confirmationPolicy) && formik.values.confirmationPolicy !== "") {
                formik.setFieldValue("confirmationPolicy", "");
            }
            if (!compatibleCancellationPolicy.includes(formik.values.cancellationPolicy) && formik.values.cancellationPolicy !== "") {
                formik.setFieldValue("cancellationPolicy", "");
            }
        },
        [  // eslint-disable-line react-hooks/exhaustive-deps
            compatibleConfirmationPolicy,
            compatibleCancellationPolicy,
            formik.values.confirmationPolicy,
            formik.values.cancellationPolicy
        ]
    );

    return <Stack spacing={1}>
        <Typography variant='h6' color='primary' fontWeight="normal">
            Condizioni prenotazione
        </Typography>
        <Typography>
            Le seguenti politiche di conferma e cancellazione verrano indicate nel preventivo.
        </Typography>

        <List>
            <ListItemButton onClick={toggleConfirmationPolicyOpen} divider disableGutters>
                <ListItemText primary={confirmationPolicy[formik.values.confirmationPolicy].title}
                              secondary="Politica di conferma"/>
                {confirmationPolicyOpen ? <ExpandLessIcon/> : <EditIcon/>}
            </ListItemButton>
            <Collapse in={confirmationPolicyOpen} timeout="auto" sx={{pl: 2}}>
                <Stack spacing={2}>
                    <RadioGroup key="confirmationPolicy"
                                aria-label={"confirmation-policy"}
                                name={"confirmationPolicy"}
                                value={formik.values.confirmationPolicy}
                                onChange={formik.handleChange}
                                sx={{width: "100%"}}
                    >
                        {
                            Object.entries(confirmationPolicy).map((
                                [policyName, policy]) =>
                                <FormControlLabel key={policyName}
                                                  label={policy.label}
                                                  value={policyName}
                                                  control={<Radio/>}
                                                  disabled={!compatibleConfirmationPolicy.includes(policyName)}/>
                            )
                        }
                    </RadioGroup>
                    {
                        formik.values.confirmationPolicy === QuoteService.enums.CONFIRMATION_POLICY.deposit &&
                        <TextField label="Importo Caparra (%)"
                                   type="number"
                                   name="depositPercentage"
                                   value={formik.values.depositPercentage}
                                   error={formik.touched.depositPercentage && Boolean(formik.errors.depositPercentage)}
                                   helperText={formik.touched.depositPercentage && formik.errors.depositPercentage}
                                   onChange={formik.handleChange}
                                   InputProps={{inputProps: {max: 100, min: 0}}}
                                   InputLabelProps={{
                                       shrink: true,
                                   }}
                        />
                    }
                    <Description value={confirmationPolicy[formik.values.confirmationPolicy]?.description}/>
                    <Divider/>
                </Stack>
            </Collapse>

            <ListItemButton onClick={toggleCancellationPolicyOpen} divider disableGutters>
                <ListItemText primary={cancellationPolicy[formik.values.cancellationPolicy].title}
                              secondary="Politica di cancellazione"/>
                {cancellationPolicyOpen ? <ExpandLessIcon/> : <EditIcon/>}
            </ListItemButton>
            <Collapse in={cancellationPolicyOpen} timeout="auto" sx={{pl: 2}}>
                <Stack spacing={2}>
                    <RadioGroup key="cancellationPolicy"
                                aria-label={"cancellation-policy"}
                                name={"cancellationPolicy"}
                                value={formik.values.cancellationPolicy}
                                onChange={formik.handleChange}
                                sx={{width: "100%"}}
                    >
                        {
                            Object.entries(cancellationPolicy).map((
                                [policyName, policy]) =>
                                <FormControlLabel key={policyName}
                                                  label={policy.label}
                                                  value={policyName}
                                                  control={<Radio/>}
                                                  disabled={!compatibleCancellationPolicy.includes(policyName)}/>
                            )
                        }
                    </RadioGroup>
                    {
                        formik.values.cancellationPolicy === QuoteService.enums.CANCELLATION_POLICY.timeRestrictedRefundable&&
                        <DatePicker value={formik.values.refundableUntil}
                                    onChange={date => formik.setFieldValue("refundableUntil", date)}
                                    disablePast
                                    maxDate={arrival}
                                    defaultCalendarMonth={today}
                                    label="Rimborsabile fino al"
                                    closeOnSelect
                                    showDaysOutsideCurrentMonth
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            required: true,
                                            error: Boolean(formik.touched.refundableUntil) && Boolean(formik.errors.refundableUntil),
                                            helperText: formik.touched.refundableUntil && formik.errors.refundableUntil
                                        }
                                    }}/>
                    }
                    <Description value={cancellationPolicy[formik.values.cancellationPolicy]?.description}/>
                    <Divider/>
                </Stack>
            </Collapse>

            <ListItemButton onClick={toggleExpirationOpen} divider disableGutters>
                <ListItemText primary={`Proposta valida fino al ${getExpiration()}`}
                              secondary="Validità proposta"/>
                {expirationOpen ? <ExpandLessIcon/> : <EditIcon/>}
            </ListItemButton>
            <Collapse in={expirationOpen} timeout="auto" sx={{pl: 2}}>
                <Stack spacing={2} mt={2}>
                    <DatePicker value={formik.values.expiration}
                                onChange={date => formik.setFieldValue("expiration", date)}
                                disablePast
                                maxDate={arrival}
                                defaultCalendarMonth={today}
                                label="Valido fino al"
                                closeOnSelect
                                showDaysOutsideCurrentMonth
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        required: true,
                                        error: Boolean(formik.touched.expiration) && Boolean(formik.errors.expiration),
                                        helperText: formik.touched.expiration && formik.errors.expiration
                                    }
                                }}
                                />
                    <Description value={
                        `Proposta valida fino alla data ${getExpiration()}. 
                         La struttura non sarà comunque riservata fino ad avvenuta conferma della prenotazione`
                    }/>
                    <Divider/>
                </Stack>
            </Collapse>

            <ListItemButton onClick={toggleTouristTaxPolicyOpen} divider disableGutters>
                <ListItemText primary={touristTaxPolicy[formik.values.touristTaxPolicy].title}
                              secondary="Tassa di soggiorno"/>
                {touristTaxPolicyOpen ? <ExpandLessIcon/> : <EditIcon/>}
            </ListItemButton>
            <Collapse in={touristTaxPolicyOpen} timeout="auto" sx={{pl: 2}}>
                <Stack spacing={2} mt={2}>
                    <RadioGroup key="touristTaxPolicy"
                                aria-label={"tourist-tax-policy"}
                                name={"touristTaxPolicy"}
                                value={formik.values.touristTaxPolicy}
                                onChange={formik.handleChange}
                                sx={{width: "100%"}}
                    >
                        {
                            Object.entries(touristTaxPolicy).map((
                                [policyName, policy]) =>
                                <FormControlLabel key={policyName}
                                                  label={policy.label}
                                                  value={policyName}
                                                  control={<Radio/>}/>
                            )
                        }
                    </RadioGroup>
                    {
                        <TextField label="Importo Tasso di Soggiorno per persona a notte"
                                   type="number"
                                   name="touristTaxUnitPrice"
                                   value={formik.values.touristTaxUnitPrice}
                                   error={formik.touched.touristTaxUnitPrice && Boolean(formik.errors.touristTaxUnitPrice)}
                                   helperText={formik.touched.touristTaxUnitPrice && formik.errors.touristTaxUnitPrice}
                                   onChange={formik.handleChange}
                                   InputProps={{inputProps: {max: 10, min: 0}}}
                                   InputLabelProps={{
                                       shrink: true,
                                   }}
                        />
                    }
                    <Description value={touristTaxPolicy[formik.values.touristTaxPolicy]?.description}/>
                    <Divider/>
                </Stack>
            </Collapse>

        </List>

        {onlinePaymentEnabled &&
            <Grid container rowSpacing={6}>
                <Grid item xs={12}>
                    <Stack direction="row" alignItems="center">
                        <Switch checked={formik.values.paymentPolicy === "online"}
                                disabled={!onlinePaymentEnabled}
                                onChange={handleChangePaymentPolicy}/>
                        <div>
                            <Typography variant="body2">
                                Prenotazione rapida (online) con carta di credito
                                {
                                    <Typography variant="body2"
                                                component="span"
                                                color={formik.values.paymentPolicy === QuoteService.enums.PAYMENT_POLICY.online ? "success.main" : "warning.main"}>
                                        {formik.values.paymentPolicy === QuoteService.enums.PAYMENT_POLICY.online ? " abilitata" : " non abilitata"}
                                    </Typography>
                                }
                            </Typography>
                            {!onlinePaymentEnabled &&
                                <Typography variant="body2" sx={{fontStyle: 'italic'}} color="primary">
                                    Funzionalità disponibile a breve
                                </Typography>}
                        </div>
                        <Tooltip enterTouchDelay={0} title={onlinePaymentDesc} leaveTouchDelay={1000}>
                            <HelpOutlineIcon/>
                        </Tooltip>
                    </Stack>
                </Grid>
            </Grid>
        }

    </Stack>
}

const Description = ({value}) => {
    return (value !== "" && value !== null && value !== undefined) ?
        <Typography variant="body2" color="text.primary">
            <Typography variant="body2"
                        display={'inline'}
                        fontWeight="bold"
                        component="span"
                        color="primary">
                Significato:
            </Typography> {value}
        </Typography>
        :
        <></>
}
export default StepBookingConditions;